/**
 * Festival and venue DTOs (fase 2, steg 2).
 *
 * ---------------------------------------------------------------------------------------------
 * A FESTIVAL IS THE CONTAINER EVERYTHING OPERATIONAL HANGS ON, AND IT HAS BEEN MISSING A DOOR.
 *
 * The table has existed since Phase 0. The seed writes two of them. Every `festival:*` cell is in
 * the matrix. The route `/festivals/$festivalId` is registered. And the sidebar's festival section
 * has rendered "Ingen festivaler ennå" this whole time — not because an organisation has none, but
 * because nothing served them. `FestivalNavSection` says so in its own header: "Phase 0 renders
 * the empty state and makes no API call: there is no festivals endpoint yet."
 *
 * This contract is that endpoint's shape.
 *
 * WHY `kind` IS ONE FIELD AND NOT TWO ENTITIES. A venue and a festival differ in what they are
 * called and how often they recur, not in what the system does with them: both hold editions, both
 * carry departments, both have a timezone that everything operational is read in. Two tables would
 * have meant two of every query. `festival_kind` is the enum, and the UI is what makes the
 * distinction legible — decision 0004 already settled that the Norwegian word is "Venue".
 *
 * THE SLUG IS GENERATED AND THEN IT STOPS MOVING. Same rule as `departments.key`, and for the same
 * reason: it is what a later import, a public volunteer link and the Phase 2 edition clone address
 * this festival by. `updateFestivalRequestSchema` therefore has no `slug`, exactly as the
 * department update has no `key`. The database enforces the shape
 * (`ck_festivals__slug_shape`) and uniqueness per organisation among rows that are not deleted.
 * ---------------------------------------------------------------------------------------------
 */
import { z } from 'zod';

import { DEFAULT_COUNTRY_CODE, DEFAULT_TIMEZONE } from '../constants.js';
import { festivalKindSchema } from '../enums.js';
import { cursorQuerySchema, page } from '../http/pagination.js';
import { urlInputSchema } from '../url.js';
import { memberPersonIdsSchema } from './edition-members.js';

export const FESTIVAL_SORT_KEYS = ['order', 'name'] as const;
export type FestivalSortKey = (typeof FESTIVAL_SORT_KEYS)[number];

export const festivalDtoSchema = z
  .object({
    id: z.uuid(),
    /** Generated once from the first name and never moved again — see the header. */
    slug: z.string(),
    name: z.string(),
    kind: festivalKindSchema,
    city: z.string().nullable(),
    countryCode: z.string().length(2),
    /**
     * Every operational time on this festival is read in this zone, not the reader's. A get-in at
     * 07:00 in Tromsø is 07:00 in Tromsø whoever opens the page and wherever they are.
     */
    timezone: z.string(),
    logoUrl: z.string().nullable(),
    websiteUrl: z.string().nullable(),
    description: z.string().nullable(),
    /** Manual ordering of the sidebar list. The list is sorted by it, then by name. */
    sortOrder: z.number().int(),
    /**
     * How many editions this festival has, so the sidebar and the list can say "3 årganger"
     * without a second request per row. Counted, never a nested array: the editions themselves
     * belong to the festival's own page.
     */
    editionCount: z.number().int().nonnegative(),
    /**
     * The next årgang that has a start date in the future, or null.
     *
     * WHY IT IS ON THE FESTIVAL AND NOT A SECOND REQUEST. `PAGES.md` asks Overview for "kort med de
     * arrangementene brukeren er tilknyttet, med nedtelling til dato", and a countdown needs a
     * date the festival itself does not have. Fetching the editions of every festival on the card
     * grid would be one request per card — the N+1 principle 6 forbids — so it is one lateral
     * beside `editionCount`, which is the shape that count already established.
     *
     * NULL IS A REAL ANSWER and there are three ways to get it: a festival with no årganger, one
     * whose årganger have no dates yet, and one whose last show has already happened. A card says
     * something different from a countdown in that case, which is the point of the field being
     * nullable rather than falling back to the newest year — "neste" that is in the past is not
     * next.
     *
     * `startsOn` IS A CALENDAR DATE and stays one: `YYYY-MM-DD`, no clock, no zone. The boundary
     * between "upcoming" and "past" is the DATABASE's own date, which can differ by a day from a
     * reader on the other side of the world for the few hours around midnight. That is accepted
     * rather than solved: the alternative is sending every årgang and letting each client decide,
     * which is the N+1 again wearing a different hat.
     */
    nextEdition: z
      .object({
        id: z.uuid(),
        name: z.string(),
        year: z.number().int(),
        startsOn: z.iso.date(),
      })
      .nullable(),
    /**
     * QA15-S funn 32: et spillesteds neste arrangement (ikke avlyst), det Oversikten teller ned til.
     * Et spillesteds år er en skjult beholder og gir aldri `nextEdition`. Null for en festival.
     */
    nextEventOn: z.iso.date().nullable().optional(),
    /** QA16 funn 39: hvilket arrangement — innslaget eller tittelen, og salen. Null uten navn er «Ikke avklart». */
    nextEvent: z
      .object({
        id: z.uuid(),
        on: z.iso.date(),
        name: z.string().nullable(),
        roomName: z.string().nullable(),
      })
      .nullable()
      .optional(),
  })
  .meta({ id: 'Festival' });

export const listFestivalsQuerySchema = cursorQuerySchema(FESTIVAL_SORT_KEYS)
  .extend({
    /** Absent means both. The sidebar groups by kind and asks for neither. */
    kind: festivalKindSchema.optional(),
  })
  .meta({ id: 'ListFestivalsQuery' });

export const festivalListResponseSchema = page(festivalDtoSchema, 'FestivalListResponse');

export const createFestivalRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    kind: festivalKindSchema.default('festival'),
    city: z.string().trim().max(80).nullish(),
    countryCode: z.string().length(2).default(DEFAULT_COUNTRY_CODE),
    /**
     * Defaulted rather than required. An organisation creating its first festival should not have
     * to answer a timezone question before it can name the thing; the default is right for every
     * Norwegian organisation and editable the moment it is not.
     */
    timezone: z.string().min(1).max(64).default(DEFAULT_TIMEZONE),
    /*
     * «godnokfestivalen.no» BLOKKERTE OPPRETTING (#16c). `z.url()` krever et skjema, og Atle sa
     * «det skal ikke ha noe å si». `urlInputSchema` godtar adressen med eller uten `www` og uten
     * skjema, og lagrer den kanoniske formen. Reglene står i `packages/shared/src/url.ts`.
     */
    websiteUrl: urlInputSchema(2048).nullish(),
    description: z.string().trim().max(2000).nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    /**
     * «HVEM ER MED?» (QA13 punkt 32). En festival har ingen årgang når den lages, så valget lagres
     * på festivalen og er FORSLAGET til den første årgangen. Senere årganger foreslår forrige årgang.
     * Utelatt: ingen foreslått.
     */
    memberPersonIds: memberPersonIdsSchema.optional(),
  })
  .meta({ id: 'CreateFestivalRequest' });

/**
 * Renaming, moving, recolouring — everything except the slug.
 *
 * Every field is optional and at least one must be present; a PATCH that changes nothing is a
 * request nobody meant to send. `kind` is included: an organisation that filed its venue as a
 * festival should be able to correct it without recreating the row and orphaning its editions.
 */
export const updateFestivalRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    kind: festivalKindSchema.optional(),
    city: z.string().trim().max(80).nullish(),
    countryCode: z.string().length(2).optional(),
    timezone: z.string().min(1).max(64).optional(),
    /**
     * Sak 215.13: skal tidssonen også settes på årgangene som finnes? Tvitvi spør når sonen
     * endres; utelatt eller `false` er «bare nye årganger», som før. Hver årgang spør
     * `edition:update` for seg, så den som ikke får endre en årgang, får et nei og ingenting skrevet.
     */
    applyTimezoneToEditions: z.boolean().optional(),
    websiteUrl: urlInputSchema(2048).nullish(),
    description: z.string().trim().max(2000).nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateFestivalRequest' });

export type FestivalDto = z.infer<typeof festivalDtoSchema>;
export type ListFestivalsQuery = z.infer<typeof listFestivalsQuerySchema>;
export type FestivalListResponse = z.infer<typeof festivalListResponseSchema>;
export type CreateFestivalRequest = z.infer<typeof createFestivalRequestSchema>;
export type UpdateFestivalRequest = z.infer<typeof updateFestivalRequestSchema>;
